/**
 * StudentLiveObserver.js
 * Observes student interactions, cursor movements, and native text selection in real-time
 * via rrweb (True Live DOM & Event Streaming) and streams compressed state packets to the watching teacher via SignalR.
 * Zero installation for student, 100% pixel-perfect replica, no permission prompt required.
 */

import { realtimeService } from "../RealtimeService";

const rrweb = require("rrweb/lib/rrweb-all.js");
const recordFn = rrweb.record || (rrweb.default && rrweb.default.record) || rrweb;
const packFn = rrweb.pack || (rrweb.default && rrweb.default.pack);

class StudentLiveObserver {
    constructor() {
        this.activeTeachers = new Map(); // teacherConnectionId -> teacherInfo
        this.isObserving = false;
        this.listeners = new Set();
        this._rrwebStopFn = null;
        this._eventQueue = [];
        this._flushTimer = null;
        this._unsubStart = null;
        this._unsubStop = null;
        this._unsubAnnotation = null;
        this._isSetup = false;
        this._pendingMeta = null;
        this._cachedInitialEvents = [];
        this._hasActiveSelection = false;
        this._selectionThrottleTimer = null;
        this._cursorThrottleTimer = null;
    }

    init() {
        if (this._isSetup || typeof window === "undefined") return;
        this._isSetup = true;

        console.log("[StudentLiveObserver] Global student live observer initialized.");

        // 1. Listen for Teacher Starting Mirror
        this._unsubStart = realtimeService.onStartStudentMirror((payload) => {
            if (!payload || !payload.teacherConnectionId) return;
            console.log("[StudentLiveObserver] Teacher connected:", payload.teacherConnectionId);
            this.activeTeachers.set(payload.teacherConnectionId, payload);
            
            // Restart clean recording to instantly emit Meta + FullSnapshot
            this._stopObserving();
            this._startObserving();
            
            this._notifyListeners("TEACHER_CONNECTED", payload);

            // Send instant cached snapshot if available
            setTimeout(() => {
                this.sendInstantSnapshot(payload.teacherConnectionId);
            }, 50);
        });

        // 2. Listen for Teacher Stopping Mirror
        this._unsubStop = realtimeService.onStopStudentMirror((payload) => {
            if (!payload) return;
            if (payload.teacherConnectionId) {
                this.activeTeachers.delete(payload.teacherConnectionId);
            } else {
                this.activeTeachers.clear();
            }

            if (this.activeTeachers.size === 0) {
                this._stopObserving();
                this._notifyListeners("TEACHER_DISCONNECTED", payload);
            }
        });

        // 3. Listen for Teacher Live Annotations (Laser, Drawing, Highlights, Tips)
        this._unsubAnnotation = realtimeService.onReceiveTeacherAnnotation((payload) => {
            if (!payload || !payload.annotation) return;
            this._notifyListeners("TEACHER_ANNOTATION", payload.annotation);
        });
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    _notifyListeners(event, data) {
        this.listeners.forEach((fn) => {
            try {
                fn(event, data);
            } catch (e) {
                console.error("[StudentLiveObserver] Listener error:", e);
            }
        });
    }

    _startObserving() {
        if (this.isObserving) return;
        this.isObserving = true;
        this._pendingMeta = null;

        window.addEventListener("beforeunload", this._handleUnload);
        window.addEventListener("pagehide", this._handleUnload);
        window.addEventListener("mousemove", this._handleMouseMove, { passive: true });
        document.addEventListener("selectionchange", this._handleSelectionChange);
        window.addEventListener("popstate", this._handleNavigation);

        try {
            if (typeof recordFn !== "function") {
                console.error("[StudentLiveObserver] recordFn is not available.");
                return;
            }

            // Start rrweb live recording: Focus on pure DOM mutations and user interactions
            this._rrwebStopFn = recordFn({
                emit: (event, isCheckout) => {
                    this._handleRrwebEmit(event, isCheckout);
                },
                recordCanvas: false,
                collectFonts: false,
                inlineStylesheet: false,
                maskAllInputs: false,
                checkoutEveryNms: 20000,
                sampling: {
                    mousemove: false, // Dedicated lightweight cursor tracking handles mouse movement smoothly without clogging DOM queue
                    mouseInteraction: true,
                    scroll: 30,
                    input: "last"
                }
            });
        } catch (err) {
            console.error("[StudentLiveObserver] Error starting rrweb record:", err);
        }
    }

    _handleNavigation = () => {
        this.triggerSnapshot(150);
    };

    triggerSnapshot(delayMs = 100) {
        if (!this.isObserving || this.activeTeachers.size === 0) return;
        setTimeout(() => {
            try {
                const takeSnap = recordFn?.takeFullSnapshot || (typeof this._rrwebStopFn === "object" && this._rrwebStopFn?.takeFullSnapshot);
                if (typeof takeSnap === "function") {
                    takeSnap(true);
                } else if (this.isObserving) {
                    this._stopObserving();
                    this._startObserving();
                }
            } catch (e) {
                console.warn("[StudentLiveObserver] Error taking full snapshot:", e);
            }
        }, delayMs);
    }

    _stopObserving() {
        if (!this.isObserving) return;
        this.isObserving = false;

        window.removeEventListener("beforeunload", this._handleUnload);
        window.removeEventListener("pagehide", this._handleUnload);
        window.removeEventListener("mousemove", this._handleMouseMove);
        document.removeEventListener("selectionchange", this._handleSelectionChange);

        if (typeof this._rrwebStopFn === "function") {
            try {
                this._rrwebStopFn();
            } catch (e) {}
            this._rrwebStopFn = null;
        }

        if (this._flushTimer) {
            clearTimeout(this._flushTimer);
            this._flushTimer = null;
        }
        if (this._selectionThrottleTimer) {
            clearTimeout(this._selectionThrottleTimer);
            this._selectionThrottleTimer = null;
        }
        if (this._cursorThrottleTimer) {
            clearTimeout(this._cursorThrottleTimer);
            this._cursorThrottleTimer = null;
        }
        this._eventQueue = [];
        this._pendingMeta = null;
        this._hasActiveSelection = false;
    }

    _handleMouseMove = (e) => {
        if (this.activeTeachers.size === 0) return;
        if (this._cursorThrottleTimer) return;

        this._cursorThrottleTimer = setTimeout(() => {
            this._cursorThrottleTimer = null;
            const width = window.innerWidth || 1;
            const height = window.innerHeight || 1;
            const x = Number(Math.min(1, Math.max(0, e.clientX / width)).toFixed(4));
            const y = Number(Math.min(1, Math.max(0, e.clientY / height)).toFixed(4));

            this.broadcastToActiveTeachers({
                type: "STUDENT_CURSOR",
                x,
                y,
                timestamp: Date.now()
            });
        }, 25); // 40 FPS real-time cursor
    };

    _handleSelectionChange = () => {
        if (this.activeTeachers.size === 0) return;

        if (this._selectionThrottleTimer) return;
        this._selectionThrottleTimer = setTimeout(() => {
            this._selectionThrottleTimer = null;

            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || !selection.rangeCount) {
                if (this._hasActiveSelection) {
                    this._hasActiveSelection = false;
                    this.broadcastToActiveTeachers({
                        type: "TEXT_SELECTION",
                        active: false,
                        ...this._getStudentContext()
                    });
                }
                return;
            }

            const text = selection.toString().trim();
            if (!text) {
                if (this._hasActiveSelection) {
                    this._hasActiveSelection = false;
                    this.broadcastToActiveTeachers({
                        type: "TEXT_SELECTION",
                        active: false,
                        ...this._getStudentContext()
                    });
                }
                return;
            }

            try {
                const range = selection.getRangeAt(0);
                const clientRects = Array.from(range.getClientRects());
                if (clientRects.length === 0) return;

                const width = window.innerWidth || 1;
                const height = window.innerHeight || 1;

                const rects = clientRects.map((r) => ({
                    x: Number((r.left / width).toFixed(4)),
                    y: Number((r.top / height).toFixed(4)),
                    width: Number((r.width / width).toFixed(4)),
                    height: Number((r.height / height).toFixed(4))
                }));

                this._hasActiveSelection = true;
                this.broadcastToActiveTeachers({
                    type: "TEXT_SELECTION",
                    active: true,
                    text,
                    rects,
                    ...this._getStudentContext()
                });
            } catch (e) {}
        }, 30);
    };

    _handleRrwebEmit = (event, isCheckout) => {
        if (this.activeTeachers.size === 0) return;

        // 1. Capture Meta (type 4)
        if (event.type === 4) {
            this._pendingMeta = event;
            return;
        }

        // 2. Capture FullSnapshot (type 2) -> Emit complete RRWEB_INIT packet!
        if (event.type === 2) {
            const rawInitEvents = this._pendingMeta ? [this._pendingMeta, event] : [event];
            this._pendingMeta = null;

            const packedEvents = rawInitEvents.map((ev) => (typeof packFn === "function" ? packFn(ev) : ev));
            this._cachedInitialEvents = packedEvents;

            const payload = {
                type: "RRWEB_INIT",
                events: packedEvents,
                isPacked: typeof packFn === "function",
                isCheckout: Boolean(isCheckout),
                ...this._getStudentContext()
            };
            this.broadcastToActiveTeachers(payload);
            return;
        }

        // 3. Incremental DOM mutation events (type 3, 5, etc.) -> Flush in 20ms micro-batches
        const packed = typeof packFn === "function" ? packFn(event) : event;
        this._eventQueue.push(packed);

        if (!this._flushTimer) {
            this._flushTimer = setTimeout(() => {
                this._flushTimer = null;
                if (this._eventQueue.length > 0) {
                    const batch = this._eventQueue.splice(0);
                    const payload = {
                        type: "RRWEB_BATCH",
                        events: batch,
                        isPacked: typeof packFn === "function",
                        ...this._getStudentContext()
                    };
                    this.broadcastToActiveTeachers(payload);
                }
            }, 20);
        }
    };

    sendInstantSnapshot(teacherConnId) {
        if (this._cachedInitialEvents && this._cachedInitialEvents.length > 0) {
            const payload = {
                type: "RRWEB_INIT",
                events: this._cachedInitialEvents,
                isPacked: typeof packFn === "function",
                ...this._getStudentContext()
            };
            if (teacherConnId) {
                realtimeService.sendStudentMirrorState(teacherConnId, payload).catch((err) => {
                    console.error("[StudentLiveObserver] Error sending snapshot to teacher:", err);
                });
            } else {
                this.broadcastToActiveTeachers(payload);
            }
        }
        try {
            const takeSnap = recordFn?.takeFullSnapshot;
            if (typeof takeSnap === "function") {
                takeSnap(true);
            }
        } catch (e) {
            console.warn("[StudentLiveObserver] Error taking full snapshot:", e);
        }
    }

    _getStudentContext() {
        if (typeof window === "undefined") return {};
        const width = window.innerWidth || 1280;
        const height = window.innerHeight || 720;
        const screenWidth = window.screen?.width || width;
        const screenHeight = window.screen?.height || height;
        const isForeground = typeof document !== "undefined" ? (document.visibilityState === "visible" && document.hasFocus()) : true;
        const connectionId = realtimeService.getConnectionId();

        return {
            connectionId,
            tabId: connectionId,
            url: window.location.pathname + window.location.search,
            title: document.title ? document.title.split("|")[0].trim() : "",
            isForeground,
            viewport: {
                width,
                height,
                screenWidth,
                screenHeight,
                devicePixelRatio: window.devicePixelRatio || 1,
                aspectRatio: Number((width / height).toFixed(2))
            },
            timestamp: Date.now()
        };
    }

    broadcastToActiveTeachers(stateData) {
        if (this.activeTeachers.size === 0) return;
        this.activeTeachers.forEach((_, teacherConnId) => {
            realtimeService.sendStudentMirrorState(teacherConnId, stateData).catch((err) => {
                console.error("[StudentLiveObserver] Error broadcasting state to teacher:", err);
            });
        });
    }

    _handleUnload = () => {
        if (this.activeTeachers.size === 0) return;
        const payload = {
            type: "WINDOW_CLOSED",
            reason: "قام الطالب بإغلاق نافذة المتصفح",
            connectionId: realtimeService.getConnectionId(),
            timestamp: Date.now()
        };
        this.broadcastToActiveTeachers(payload);
    };
}

export const studentLiveObserver = new StudentLiveObserver();
export default studentLiveObserver;
